import * as Yup from 'yup';
import {Asserts} from 'yup';
import {AllocationAccountType} from '@/repositories/beneva-api/endpoints/allocations';

const assertUniqueAccounts = (value: string, ctx: Yup.TestContext<Yup.AnyObject>) => {
  const context = ctx as Yup.TestContext<AllocationFormSchemaType>;
  const accounts = context.options.context?.accounts ?? [];
  const hasDuplicates = accounts.filter(account => account.accountId === value).length > 1;

  if (hasDuplicates) {
    return ctx.createError({message: 'errors.accounts.uniqueness'});
  }

  return true;
};

const toValidPercentage = (value: number | string): number | undefined => {
  const parsed = parseInt(`${value}`, 10);
  return isNaN(parsed) ? undefined : parsed;
};

export interface AllocationFormSchemaType extends Asserts<typeof AllocationFormSchema> {}

export enum AllocationFormSchemaErrors {
  REQUIRED = 'errors.required',
  NO_DANGEROUS_CONTENT = 'errors.content-not-allowed',
  AMOUNT_TOTAL_MIN = 'errors.amount-total.min',
  AMOUNT_TOTAL_MAX = 'errors.amount-total.max',
  MUST_BE_POSITIVE = 'errors.must-be-positive',
  AMOUNT_MAX = 'errors.amount.max',
  ACCOUNTS_UNIQUENESS = 'errors.accounts.uniqueness',
}

export const AllocationFormSchema = Yup.object().shape({
  name: Yup.string()
    .noDangerousContent(AllocationFormSchemaErrors.NO_DANGEROUS_CONTENT)
    .required(AllocationFormSchemaErrors.REQUIRED),
  amountTotal: Yup.number()
    .min(100, AllocationFormSchemaErrors.AMOUNT_TOTAL_MIN)
    .max(100, AllocationFormSchemaErrors.AMOUNT_TOTAL_MAX)
    .required(AllocationFormSchemaErrors.REQUIRED),
  accountType: Yup.string().oneOf(Object.values(AllocationAccountType)).required(AllocationFormSchemaErrors.REQUIRED),
  accounts: Yup.array()
    .of(
      Yup.object().shape({
        accountId: Yup.string().required(AllocationFormSchemaErrors.REQUIRED).test({
          name: 'unique-accounts',
          skipAbsent: true,
          test: assertUniqueAccounts,
        }),
        amount: Yup.number()
          .positive(AllocationFormSchemaErrors.MUST_BE_POSITIVE)
          .max(100, AllocationFormSchemaErrors.AMOUNT_MAX)
          .transform(toValidPercentage)
          .required(AllocationFormSchemaErrors.REQUIRED),
      }),
    )
    .default([]),
});
